import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import i18n from "../../config/i18n";
// Migrated to shared API client to avoid direct axios usage
import { sharedApi, getAuthToken } from "../../services/sharedApi";
import { toApiError } from "../../utils/errorUtils";

import type { ProfileData, ProgressStats } from "../../features/profile/types";
import type { UserProfile } from "@lingoshade/api";
import type { PayloadAction } from "@reduxjs/toolkit";

export interface ProfileState {
  profileData: ProfileData;
  dirty: boolean;
  statsRequestId: string | null;
  progressStats: ProgressStats | null;
  loadingProfile: boolean;
  loadingStats: boolean;
  savingProfile: boolean;
  errorProfile: string | null;
  errorStats: string | null;
  errorSave: string | null;
}

export const toProfileData = (user: UserProfile): ProfileData => ({
  firstName: user.firstName ?? "",
  lastName: user.lastName ?? "",
  nativeLanguage: user.nativeLanguage ?? "",
  targetLanguage: user.targetLanguage ?? "",
  level: user.level ?? "",
});

const initialState: ProfileState = {
  dirty: false,
  statsRequestId: null,
  profileData: {
    firstName: "",
    lastName: "",
    nativeLanguage: "",
    targetLanguage: "",
    level: "",
  },
  progressStats: null,
  loadingProfile: false,
  loadingStats: false,
  savingProfile: false,
  errorProfile: null,
  errorStats: null,
  errorSave: null,
};

// Thunk to fetch user profile
export const fetchUserProfile = createAsyncThunk<
  ProfileData,
  void,
  { rejectValue: string }
>("profile/fetchUserProfile", async (_, { rejectWithValue }) => {
  try {
    const token = getAuthToken();
    const resp = await sharedApi.user.getProfile(token);
    // shared endpoint returns { user: UserProfile }
    return toProfileData(resp.user);
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(
      apiError.message || i18n.t("profile:errors.failedToLoadUserProfile"),
    );
  }
});

// Thunk to fetch progress stats
export const fetchProgressStats = createAsyncThunk<
  ProgressStats,
  void,
  { rejectValue: string }
>("profile/fetchProgressStats", async (_, { rejectWithValue }) => {
  try {
    const token = getAuthToken();
    const stats = await sharedApi.user.getEnhancedStats(token);
    return stats;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(
      apiError.message || i18n.t("profile:errors.failedToLoadProgressStats"),
    );
  }
});

// Thunk to update profile
export const updateProfile = createAsyncThunk<
  UserProfile,
  ProfileData,
  { rejectValue: string }
>("profile/updateProfile", async (data, { rejectWithValue }) => {
  try {
    const token = getAuthToken();
    const resp = await sharedApi.user.updateProfile(data, token);
    return resp.user;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(
      apiError.message || i18n.t("profile:errors.failedToUpdateProfile"),
    );
  }
});

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    initializeProfile: (state, action: PayloadAction<ProfileData>) => {
      state.profileData = action.payload;
      state.dirty = false;
    },
    syncProfile: (state, action: PayloadAction<ProfileData>) => {
      if (!state.dirty) state.profileData = action.payload;
    },
    setProfileDataField: (
      state,
      action: PayloadAction<{ field: keyof ProfileData; value: string }>,
    ) => {
      const { field, value } = action.payload;
      state.profileData[field] = value;
      state.dirty = true;
    },
    resetProfileErrors: (state) => {
      state.errorProfile = null;
      state.errorStats = null;
      state.errorSave = null;
    },
    resetProfileState: () => initialState,
    resetProgressStats: (state): void => {
      state.progressStats = null;
      state.statsRequestId = null;
      state.loadingStats = false;
      state.errorStats = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.loadingProfile = true;
        state.errorProfile = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loadingProfile = false;
        state.profileData = action.payload;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loadingProfile = false;
        state.errorProfile =
          action.payload || i18n.t("profile:errors.failedToLoadUserProfile");
      })
      .addCase(fetchProgressStats.pending, (state, action) => {
        state.statsRequestId = action.meta.requestId;
        state.loadingStats = true;
        state.errorStats = null;
      })
      .addCase(fetchProgressStats.fulfilled, (state, action) => {
        if (state.statsRequestId !== action.meta.requestId) return;
        state.statsRequestId = null;
        state.loadingStats = false;
        state.progressStats = action.payload;
      })
      .addCase(fetchProgressStats.rejected, (state, action) => {
        if (state.statsRequestId !== action.meta.requestId) return;
        state.statsRequestId = null;
        state.loadingStats = false;
        state.errorStats =
          action.payload || i18n.t("profile:errors.failedToLoadProgressStats");
      })
      .addCase(updateProfile.pending, (state) => {
        state.savingProfile = true;
        state.errorSave = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.savingProfile = false;
        state.profileData = toProfileData(action.payload);
        state.dirty = false;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.savingProfile = false;
        state.errorSave =
          action.payload || i18n.t("profile:errors.failedToUpdateProfile");
      });
  },
});

export const {
  setProfileDataField,
  resetProfileErrors,
  initializeProfile,
  syncProfile,
  resetProfileState,
  resetProgressStats,
} = profileSlice.actions;
export default profileSlice.reducer;
